// src/contexts/PageContentContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetPageContentQuery } from '../features/api/publicApiSlice';

interface PageContent {
  title?: string;
  metaDescription?: string;
  metaKeywords?: string;
  jsonLdData?: string;
  scripts?: Array<{
    content: string;
    placement: string;
    order: number;
  }>;
  banners?: Array<{
    url: string;
    order: number;
  }>;
}

interface PageContentContextType {
  content: PageContent;
  loading: boolean;
  error: Error | null;
}

const PageContentContext = createContext<PageContentContextType>({
  content: {},
  loading: false,
  error: null,
});

export const PageContentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [content, setContent] = useState<PageContent>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const location = useLocation();

  const { data, error: apiError, isLoading } = useGetPageContentQuery(location.pathname, {
    skip: !location.pathname,
  });

  useEffect(() => {
    if (data) {
      setContent(data);
    }
    if (apiError) {
      setError(apiError as Error);
    }
  }, [data, apiError]);

  return (
    <PageContentContext.Provider value={{ content, loading, error }}>
      {children}
    </PageContentContext.Provider>
  );
};

export const usePageContent = () => useContext(PageContentContext);